import React, { useMemo } from 'react';
import { MatterData } from './MatterDetails';
import DetailsTabContent from './tabs/Details';
import SourcesTabContent from './tabs/Sources';
import QAsTabContent from './tabs/QAs';
import NotesTabContent from './tabs/Notes';
import TasksTabContent from './tabs/Tasks';
import MatterEconomicsWorkspace from './economics/MatterEconomicsWorkspace';
import { MatterClient } from './MatterClients';
import {
  FeatureTour,
  matterDetailsTabTourSteps,
  matterDocumentsTabTourSteps,
  matterQAsTabTourSteps,
  matterTasksTabTourSteps,
  matterNotesTabTourSteps,
} from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

interface MatterDetailsContentProps {
  activeTab: string;
  matterData: MatterData | null;
  userType: string;
  userAccessLevel: 'edit' | 'admin' | 'view';
  isEditingMatterInfo: boolean;
  setIsEditingMatterInfo: (editing: boolean) => void;
  editedMatterFields: any;
  setEditedMatterFields: (fields: any) => void;
  handleSaveMatterInfo: () => Promise<void>;
  onUpdate: (patch?: Record<string, any>) => void;
  matterStatuses?: Array<{ label: string; value: string }>;
  sources: any;
  sourcesLoading: boolean;
  setSources: React.Dispatch<React.SetStateAction<any>>;
  questionnaire: any[];
  questionnaireLoading: boolean;
  notes: any[];
  notesLoading: boolean;
  setNotes: React.Dispatch<React.SetStateAction<any[]>>;
  clients?: MatterClient[];
  suggestedTasks: any[];
  setSuggestedTasks: React.Dispatch<React.SetStateAction<any[]>>;
  matterTasks: any[];
  setMatterTasks: React.Dispatch<React.SetStateAction<any[]>>;
  suggestedTasksLoading: boolean;
  matterTasksLoading: boolean;
  hasDocuments: boolean;
  /** Lets a tab hand focus to another one (the Overview's "All tasks"). */
  onNavigateTab?: (tab: string) => void;
}

const MatterDetailsContent: React.FC<MatterDetailsContentProps> = ({
  activeTab,
  matterData,
  userType,
  userAccessLevel,
  isEditingMatterInfo,
  setIsEditingMatterInfo,
  editedMatterFields,
  setEditedMatterFields,
  handleSaveMatterInfo,
  onUpdate,
  matterStatuses = [],
  sources,
  sourcesLoading,
  setSources,
  questionnaire,
  questionnaireLoading,
  notes,
  notesLoading,
  setNotes,
  clients = [],
  suggestedTasks,
  setSuggestedTasks,
  matterTasks,
  setMatterTasks,
  suggestedTasksLoading,
  matterTasksLoading,
  hasDocuments,
  onNavigateTab,
}) => {

  // ── Per-Tab Tour Mapping ──────────────────────────────────────
  const tabTourMap: Record<string, { id: string; steps: any[] }> = useMemo(() => ({
    Details:   { id: 'matter-tab-details',   steps: matterDetailsTabTourSteps },
    Documents: { id: 'matter-tab-documents', steps: matterDocumentsTabTourSteps },
    'Q&As':    { id: 'matter-tab-qas',       steps: matterQAsTabTourSteps },
    Tasks:     { id: 'matter-tab-tasks',     steps: matterTasksTabTourSteps },
    Notes:     { id: 'matter-tab-notes',     steps: matterNotesTabTourSteps },
  }), []);

  const currentTabTour = tabTourMap[activeTab];
  const {
    isRunning: isTabTourRunning,
    handleJoyrideCallback: handleTabTourCallback,
  } = useFeatureTour({
    tourId: currentTabTour?.id || 'matter-tab-none',
    steps: currentTabTour?.steps || [],
    readyToShow: !!currentTabTour,
    autoStartDelay: 400,
  });

  const renderContent = () => {
    switch (activeTab) {
      case 'Details':
        return (
          <DetailsTabContent
            matterData={matterData}
            userType={userType}
            userAccessLevel={userAccessLevel}
            isEditingMatterInfo={isEditingMatterInfo}
            setIsEditingMatterInfo={setIsEditingMatterInfo}
            editedMatterFields={editedMatterFields}
            setEditedMatterFields={setEditedMatterFields}
            handleSaveMatterInfo={handleSaveMatterInfo}
            matterStatuses={matterStatuses}
            onUpdate={onUpdate}
            clients={clients}
            hasDocuments={hasDocuments}
            sources={sources}
            matterTasks={matterTasks}
            suggestedTasks={suggestedTasks}
            onNavigateTab={onNavigateTab}
          />
        );
      case 'Documents':
        return (
          <SourcesTabContent
            matterData={matterData}
            sources={sources}
            setSources={setSources}
            loading={sourcesLoading}
            userAccessLevel={userAccessLevel}
            onUpdate={onUpdate}
            hasDocuments={hasDocuments}
          />
        );
      case 'Q&As':
        return (
          <QAsTabContent
            matterData={matterData}
            questionnaire={questionnaire}
            loading={questionnaireLoading}
            userAccessLevel={userAccessLevel}
            onUpdate={onUpdate}
            hasDocuments={hasDocuments}
          />
        );
      case 'Notes':
        return (
        <NotesTabContent
            matterData={matterData}
            notes={notes}
            setNotes={setNotes}
            userAccessLevel={userAccessLevel}
            onUpdate={onUpdate}
            loading={notesLoading}
            hasDocuments={hasDocuments}
          />
        );
      case 'Tasks':
        return (
          <TasksTabContent
            matterData={matterData}
            suggestedTasks={suggestedTasks}
            setSuggestedTasks={setSuggestedTasks}
            matterTasks={matterTasks}
            setMatterTasks={setMatterTasks}
            suggestedTasksLoading={suggestedTasksLoading}
            matterTasksLoading={matterTasksLoading}
            userAccessLevel={userAccessLevel}
            onUpdate={onUpdate}
            hasDocuments={hasDocuments}
          />
        );
      case 'Economics':
        return (
          <MatterEconomicsWorkspace
            matterData={matterData}
            userAccessLevel={userAccessLevel}
            onUpdate={onUpdate}
          />
        );
      default:
        return (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            Select a tab to view content
          </div>
        );
    }
  };

  // Wrap each tab's content with a data-tour-id so the orientation tour can target it
  const tourId = `matter-tab-${activeTab.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-')}-content`;

  return (
    <div className="min-h-0 flex-1">
      <div data-tour-id={tourId} className="h-full">
        {renderContent()}
      </div>
      {currentTabTour && (
        <FeatureTour
          steps={currentTabTour.steps}
          run={isTabTourRunning}
          onCallback={handleTabTourCallback}
          disableOverlay
        />
      )}
    </div>
  );
};

export default MatterDetailsContent;
